'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Scan } from 'lucide-react';

const navLinks = [
  { label: 'Home', href: '/home-page' },
  { label: 'About Us', href: '/about-page' },
  { label: 'How It Works', href: '/how-it-works' },
  { label: 'FAQ', href: '/faq' },
];

export default function Navbar() {
  const pathname = usePathname();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-soft border-b border-slate-100'
            : 'bg-white/80 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 xl:px-16">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link href="/home-page" className="flex items-center gap-2 group">
              <AppLogo size={36} />
              <span className="font-bold text-xl bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent tracking-tight">
                Insighta
              </span>
            </Link>

            {/* Center Nav — Desktop */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks?.map((link) => {
                const isActive = pathname === link?.href;
                return (
                  <Link
                    key={link?.href}
                    href={link?.href}
                    className={`relative px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                      isActive
                        ? 'text-blue-600 bg-blue-50' :'text-slate-600 hover:text-blue-600 hover:bg-blue-50/70'
                    }`}
                  >
                    {link?.label}
                    {isActive && (
                      <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-blue-500 rounded-full" />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Right CTA */}
            <div className="hidden md:flex items-center gap-3">
              <Link
                href="/upload-page"
                className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-full px-5 py-2.5 text-sm shadow-blue hover:shadow-blue-strong hover:from-blue-600 hover:to-blue-700 transition-all duration-200 active:scale-95"
              >
                <Scan size={16} />
                Start Scan
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors"
              aria-label="Toggle mobile menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>
      {/* Mobile Drawer */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/20 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
        {/* Drawer */}
        <div
          className={`absolute top-0 right-0 h-full w-72 bg-white shadow-strong transition-transform duration-300 ${
            mobileOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between px-6 h-16 border-b border-slate-100">
            <span className="font-bold text-lg text-blue-600">Menu</span>
            <button
              onClick={() => setMobileOpen(false)}
              className="p-2 rounded-xl text-slate-500 hover:bg-slate-100"
            >
              <X size={20} />
            </button>
          </div>
          <nav className="px-4 py-6 flex flex-col gap-1">
            {navLinks?.map((link) => {
              const isActive = pathname === link?.href;
              return (
                <Link
                  key={link?.href}
                  href={link?.href}
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-blue-50 text-blue-600' :'text-slate-600 hover:bg-slate-50 hover:text-blue-600'
                  }`}
                >
                  {link?.label}
                </Link>
              );
            })}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <Link
                href="/upload-page"
                className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-full px-5 py-3 text-sm shadow-blue"
              >
                <Scan size={16} />
                Start Scan
              </Link>
            </div>
          </nav>
        </div>
      </div>
      {/* Spacer */}
      <div className="h-16" />
    </>
  );
}